/**
 * XState-based Character State Machine
 * Part of Sprint 0023: Unified State Observability System
 * 
 * This replaces the fragmented StateMachine + CombatSystem state tracking
 * with a unified hierarchical/parallel state machine.
 */

import { setup, assign, createActor, type AnyActorRef } from 'xstate';
import { Vector3 } from '@babylonjs/core';
import type { CharacterStateContext, CharacterEvent, CharacterDebugSnapshot } from './types';
import type { StatePhase } from '../config/characters';
import { logger } from '../core/Logger';
import { ActionHandlerRegistry } from './ActionHandlers';

// ============================================================================
// Physics Handler Registry (for requesting jumps and movement)
// ============================================================================

export interface CharacterPhysicsHandler {
  requestJump: () => void;
  setDesiredMovement: (x: number, z: number) => void;
  getIsGrounded: () => boolean;
}

const physicsHandlers = new Map<string, CharacterPhysicsHandler>();

export function registerPhysicsHandler(characterId: string, handler: CharacterPhysicsHandler): void {
  physicsHandlers.set(characterId, handler);
  logger.core.debug(`[CharacterMachine] Registered physics handler for ${characterId}`);
}

export function unregisterPhysicsHandler(characterId: string): void {
  physicsHandlers.delete(characterId);
}

export function getPhysicsHandler(characterId: string): CharacterPhysicsHandler | undefined {
  return physicsHandlers.get(characterId);
}

// ============================================================================
// Character State Machine Definition
// ============================================================================

export const characterMachine = setup({
  types: {
    context: {} as CharacterStateContext,
    events: {} as CharacterEvent,
    input: {} as { characterId: string; maxHealth: number },
  },
  actions: {
    // Animation actions - call ActionHandlerRegistry
    playIdleAnimation: ({ context }) => {
      // Don't play locomotion animations during FULL-BODY attacks
      // Masked attacks (upper body) should allow locomotion animations to continue on lower body
      const isFullBodyAttack = context.activeAttack && !context.activeAttack.attackDef.maskBones;
      if (isFullBodyAttack) {
        logger.core.debug(`[CharacterMachine] ${context.characterId}: Skipping idle animation (full-body attack in progress)`);
        return;
      }
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play idle animation`);
      ActionHandlerRegistry.playIdleAnimation(context.characterId);
    },
    
    playWalkAnimation: ({ context }) => {
      // Don't play locomotion animations during FULL-BODY attacks
      // Masked attacks (upper body) should allow locomotion animations to continue on lower body
      const isFullBodyAttack = context.activeAttack && !context.activeAttack.attackDef.maskBones;
      if (isFullBodyAttack) {
        logger.core.debug(`[CharacterMachine] ${context.characterId}: Skipping walk animation (full-body attack in progress)`);
        return;
      }
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play walk animation`);
      ActionHandlerRegistry.playWalkAnimation(context.characterId, context.movementInput);
    },
    
    playJumpAnimation: ({ context }) => {
      // Don't play jump animation during FULL-BODY attacks
      const isFullBodyAttack = context.activeAttack && !context.activeAttack.attackDef.maskBones;
      if (isFullBodyAttack) {
        logger.core.debug(`[CharacterMachine] ${context.characterId}: Skipping jump animation (full-body attack in progress)`);
        return;
      }
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play jump animation`);
      ActionHandlerRegistry.playJumpAnimation(context.characterId);
      // Request jump from physics
      const handler = physicsHandlers.get(context.characterId);
      if (handler) {
        handler.requestJump();
      }
    },
    
    playFallAnimation: ({ context }) => {
      // Don't play fall animation during FULL-BODY attacks
      const isFullBodyAttack = context.activeAttack && !context.activeAttack.attackDef.maskBones;
      if (isFullBodyAttack) {
        logger.core.debug(`[CharacterMachine] ${context.characterId}: Skipping fall animation (full-body attack in progress)`);
        return;
      }
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play fall animation`);
      ActionHandlerRegistry.playFallAnimation(context.characterId);
    },
    
    playLandAnimation: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play land animation`);
      ActionHandlerRegistry.playLandAnimation(context.characterId);
    },
    
    playTurnAnimation: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play turn animation`);
      ActionHandlerRegistry.playTurnAnimation(context.characterId, context.movementInput);
    },
    
    playAttackAnimation: ({ context }) => {
      const attackId = context.activeAttack?.attackDef?.id ?? 'unknown';
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Play attack animation: ${attackId}`);
      ActionHandlerRegistry.playAttackAnimation(context.characterId, attackId, 'windup');
    },
    
    // Combat actions
    attachWeapon: ({ context }) => {
      const attackId = context.activeAttack?.attackDef?.id ?? 'unknown';
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Attach weapon`);
      ActionHandlerRegistry.attachWeapon(context.characterId, attackId);
    },
    
    enableHitbox: ({ context }) => {
      const attackId = context.activeAttack?.attackDef?.id ?? 'unknown';
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Enable hitbox`);
      ActionHandlerRegistry.enableHitbox(context.characterId, attackId);
    },
    
    disableHitbox: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Disable hitbox`);
      ActionHandlerRegistry.disableHitbox(context.characterId);
    },
    
    detachProjectile: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Detach projectile`);
      ActionHandlerRegistry.detachWeapon(context.characterId);
    },
    
    cleanupAttack: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Cleanup attack`);
    },
    
    resumeLocomotionAnimation: ({ context }) => {
      // Re-sync locomotion animation after combat ends
      // Since combat and locomotion are parallel, the state doesn't change, so we need to manually re-trigger
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Resume locomotion animation`);
      
      // Query CURRENT input state from character controller (don't use stale context)
      const character = ActionHandlerRegistry['characters']?.get(context.characterId);
      const currentInput = character?.getInputDirection() ?? 0;
      
      // Query CURRENT grounded state from character controller (context.isGrounded may be stale)
      const isGrounded = character?.isGrounded ?? context.isGrounded;
      
      logger.core.debug(`[CharacterMachine] Current input state: ${currentInput}, isGrounded: ${isGrounded} (context: ${context.isGrounded})`);
      
      // Check current locomotion state and play appropriate animation based on CURRENT input
      if (currentInput !== 0) {
        ActionHandlerRegistry.playWalkAnimation(context.characterId, currentInput);
      } else if (isGrounded) {
        ActionHandlerRegistry.playIdleAnimation(context.characterId);
      } else if (context.velocity.y < 0) {
        ActionHandlerRegistry.playFallAnimation(context.characterId);
      } else {
        // Fallback: if no other condition matches, play idle (safe default)
        logger.core.warn(`[CharacterMachine] ${context.characterId}: No locomotion condition matched, defaulting to idle`);
        ActionHandlerRegistry.playIdleAnimation(context.characterId);
      }
    },
    
    // Damage actions
    applyDamage: ({ context, event }) => {
      if (event.type === 'DAMAGE_RECEIVED') {
        logger.core.debug(`[CharacterMachine] ${context.characterId}: Apply damage: ${event.amount}`);
      }
    },
    
    triggerDeath: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Trigger death`);
      ActionHandlerRegistry.triggerDeath(context.characterId);
    },

    onPathConstraintEnabled: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Path constraint enabled`);
      ActionHandlerRegistry.onPathConstraintEnabled(context.characterId);
    },

    onPathConstraintDisabled: ({ context }) => {
      logger.core.debug(`[CharacterMachine] ${context.characterId}: Path constraint disabled`);
      ActionHandlerRegistry.onPathConstraintDisabled(context.characterId);
    },
  },
  guards: {
    canJump: ({ context }) => {
      return context.isGrounded;
    },
    
    canAttack: ({ context, event }) => {
      if (event.type !== 'INPUT_ATTACK') return false;
      
      const now = performance.now() / 1000;
      
      // Check global cooldown
      if (now < context.globalCooldownEnd) {
        return false;
      }
      
      // Check attack-specific cooldown
      const cooldownEnd = context.attackCooldowns.get(event.attackId);
      if (cooldownEnd && now < cooldownEnd) {
        return false;
      }
      
      return true;
    },
    
    isGrounded: ({ context }) => {
      return context.isGrounded;
    },
    
    isAirborne: ({ context }) => {
      return !context.isGrounded;
    },
    
    hasMovementInput: ({ context }) => {
      const result = context.movementInput !== 0;
      logger.character.log(`[Guard] hasMovementInput: movementInput=${context.movementInput}, result=${result}`);
      return result;
    },
    
    noMovementInput: ({ context }) => {
      return context.movementInput === 0;
    },
    
    isFalling: ({ context }) => {
      return context.velocity.y < 0;
    },
    
    isDead: ({ context }) => {
      return context.health <= 0;
    },
    
    isPhaseWindup: ({ event }) => {
      return event.type === 'ANIM_PHASE_CHANGE' && event.phase === 'windup';
    },
    
    isPhaseActive: ({ event }) => {
      return event.type === 'ANIM_PHASE_CHANGE' && event.phase === 'active';
    },
    
    isPhaseRecovery: ({ event }) => {
      return event.type === 'ANIM_PHASE_CHANGE' && event.phase === 'recovery';
    },
  },
}).createMachine({
  id: 'character',
  type: 'parallel',
  context: ({ input }) => ({
    characterId: input.characterId,
    position: Vector3.Zero(),
    velocity: Vector3.Zero(),
    isGrounded: true,
    movementInput: 0,
    facingDirection: 1,
    activeAttack: null,
    health: input.maxHealth,
    maxHealth: input.maxHealth,
    attackCooldowns: new Map(),
    globalCooldownEnd: 0,
    animationNormalizedTime: 0,
  }),
  
  states: {
    // ========================================================================
    // LOCOMOTION REGION (parallel)
    // ========================================================================
    locomotion: {
      initial: 'idle',
      states: {
        idle: {
          entry: 'playIdleAnimation',
          on: {
            INPUT_MOVE: {
              target: 'walking',
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_JUMP: {
              target: 'jumping',
              guard: 'canJump',
            },
            PHYSICS_AIRBORNE: 'falling',
            SYNC_LOCOMOTION: {
              target: 'idle',
              reenter: true,
            },
          },
        },
        
        walking: {
          entry: 'playWalkAnimation',
          on: {
            INPUT_MOVE: {
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_STOP: 'idle',
            INPUT_JUMP: {
              target: 'jumping',
              guard: 'canJump',
            },
            PHYSICS_AIRBORNE: 'falling',
            SYNC_LOCOMOTION: {
              target: 'walking',
              reenter: true,
            },
          },
        },
        
        jumping: {
          entry: 'playJumpAnimation',
          on: {
            PHYSICS_AIRBORNE: 'falling',
            PHYSICS_LANDED: 'landing',
            PHYSICS_GROUNDED: 'landing',
            // Track movement input while airborne (for correct landing transition)
            INPUT_MOVE: {
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_STOP: {
              actions: assign({
                movementInput: () => 0,
              }),
            },
            SYNC_LOCOMOTION: {
              target: 'jumping',
              reenter: true,
            },
          },
        },
        
        falling: {
          entry: 'playFallAnimation',
          on: {
            PHYSICS_LANDED: 'landing',
            PHYSICS_GROUNDED: 'landing', // Also accept GROUNDED from sync
            // Track movement input while airborne (for correct landing transition)
            INPUT_MOVE: {
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_STOP: {
              actions: assign({
                movementInput: () => 0,
              }),
            },
            SYNC_LOCOMOTION: {
              target: 'falling',
              reenter: true,
            },
          },
        },
        
        landing: {
          entry: 'playLandAnimation',
          on: {
            // Landing animation queries current input and sends INPUT_MOVE or INPUT_STOP
            INPUT_MOVE: {
              target: 'walking',
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_STOP: {
              target: 'idle',
              actions: assign({
                movementInput: () => 0,
              }),
            },
            SYNC_LOCOMOTION: {
              target: 'landing',
              reenter: true,
            },
          },
        },
        
        // TODO: Turning state requires direction-change detection to trigger
        // Currently no transitions to this state - needs INPUT_TURN event or direction change logic
        turning: {
          entry: 'playTurnAnimation',
          on: {
            // Turn animation queries current input and sends INPUT_MOVE or INPUT_STOP
            INPUT_MOVE: {
              target: 'walking',
              actions: assign({
                movementInput: ({ event }) => event.direction,
              }),
            },
            INPUT_STOP: {
              target: 'idle',
              actions: assign({
                movementInput: () => 0,
              }),
            },
          },
        },
      },
    },
    
    // ========================================================================
    // COMBAT REGION (parallel)
    // ========================================================================
    combat: {
      initial: 'ready',
      states: {
        ready: {
          on: {
            INPUT_ATTACK: {
              target: 'attacking',
              guard: 'canAttack',
              actions: [
                assign({
                  activeAttack: ({ event }) => ({
                    attackDef: event.attackDef,
                    startTime: performance.now() / 1000,
                    normalizedTime: 0,
                    phase: null as StatePhase | null,
                    processedEvents: new Set<number>(),
                  }),
                }),
                'playAttackAnimation',
              ],
            },
            // Ignore stale ATTACK_END events that arrive after cooldown transition
            ATTACK_END: {},
            // Ignore stale phase change events from previous attack animations
            ANIM_PHASE_CHANGE: {},
            RECOVERY_COMPLETE: {},
          },
        },
        
        attacking: {
          initial: 'windup',
          
          // Shared attack state handlers
          on: {
            ANIM_TIME_UPDATE: {
              actions: assign({
                animationNormalizedTime: ({ event }) => event.normalizedTime,
                activeAttack: ({ context, event }) => {
                  if (!context.activeAttack) return null;
                  return {
                    ...context.activeAttack,
                    normalizedTime: event.normalizedTime,
                  };
                },
              }),
            },
            
            ATTACK_END: {
              target: 'cooldown',
              actions: 'cleanupAttack',
            },
            
            DAMAGE_RECEIVED: {
              // Interrupt attack on damage (configurable via interruptionPolicy)
              target: 'ready',
              actions: ['cleanupAttack', 'applyDamage'],
            },
          },
          
          states: {
            windup: {
              entry: 'attachWeapon',
              on: {
                ANIM_PHASE_CHANGE: {
                  target: 'active',
                  guard: 'isPhaseActive',
                  actions: assign({
                    activeAttack: ({ context }) => {
                      if (!context.activeAttack) return null;
                      return { ...context.activeAttack, phase: 'active' as StatePhase };
                    },
                  }),
                },
              },
            },
            
            active: {
              entry: 'enableHitbox',
              exit: 'disableHitbox',
              on: {
                ANIM_PHASE_CHANGE: {
                  target: 'recovery',
                  guard: 'isPhaseRecovery',
                  actions: assign({
                    activeAttack: ({ context }) => {
                      if (!context.activeAttack) return null;
                      return { ...context.activeAttack, phase: 'recovery' as StatePhase };
                    },
                  }),
                },
                
                ATTACK_HIT: {
                  actions: assign({
                    activeAttack: ({ context }) => {
                      if (!context.activeAttack?.weaponInstance) return context.activeAttack;
                      return {
                        ...context.activeAttack,
                        weaponInstance: {
                          ...context.activeAttack.weaponInstance,
                          hasHit: true,
                        },
                      };
                    },
                  }),
                },
              },
            },
            
            recovery: {
              on: {
                RECOVERY_COMPLETE: {
                  target: '#character.combat.cooldown',
                },
              },
            },
          },
        },
        
        cooldown: {
          entry: assign({
            attackCooldowns: ({ context }) => {
              if (!context.activeAttack) return context.attackCooldowns;
              const attackDef = context.activeAttack.attackDef;
              const now = performance.now() / 1000;
              const newCooldowns = new Map(context.attackCooldowns);
              
              if (attackDef.cooldown) {
                newCooldowns.set(attackDef.id, now + attackDef.cooldown);
              }
              
              return newCooldowns;
            },
            globalCooldownEnd: ({ context }) => {
              if (!context.activeAttack) return context.globalCooldownEnd;
              const attackDef = context.activeAttack.attackDef;
              const now = performance.now() / 1000;
              
              if (attackDef.globalCooldown) {
                return now + attackDef.globalCooldown;
              }
              return context.globalCooldownEnd;
            },
            activeAttack: null,
          }),
          
          on: {
            // Allow attacks to be started from cooldown state (if cooldown check passes)
            INPUT_ATTACK: {
              target: 'attacking',
              guard: 'canAttack',
              actions: [
                assign({
                  activeAttack: ({ event }) => ({
                    attackDef: event.attackDef,
                    startTime: performance.now() / 1000,
                    normalizedTime: 0,
                    phase: null as StatePhase | null,
                    processedEvents: new Set<number>(),
                  }),
                }),
                'playAttackAnimation',
              ],
            },
            // Ignore stale events from previous attack
            ATTACK_END: {},
            ANIM_PHASE_CHANGE: {},
            RECOVERY_COMPLETE: {},
          },
          
          after: {
            // Auto-transition back to ready after brief cooldown check delay
            50: {
              target: 'ready',
              actions: 'resumeLocomotionAnimation',
            },
          },
        },
      },
    },
    
    // ========================================================================
    // HEALTH REGION (parallel)
    // ========================================================================
    health: {
      initial: 'alive',
      states: {
        alive: {
          on: {
            DAMAGE_RECEIVED: [
              {
                // If this damage kills, transition to dead
                target: 'dead',
                guard: ({ context, event }) => (context.health - event.amount) <= 0,
                actions: assign({
                  health: () => 0,
                }),
              },
              {
                // Otherwise just update health
                actions: assign({
                  health: ({ context, event }) => Math.max(0, context.health - event.amount),
                }),
              },
            ],
            HEAL: {
              actions: assign({
                health: ({ context, event }) => Math.min(context.maxHealth, context.health + event.amount),
              }),
            },
            DEATH: 'dead',
          },
          always: {
            target: 'dead',
            guard: 'isDead',
          },
        },
        
        dead: {
          entry: 'triggerDeath',
          on: {
            RESPAWN: {
              target: 'alive',
              actions: assign({
                health: ({ context }) => context.maxHealth,
              }),
            },
          },
        },
      },
    },
  },

  on: {
    PATH_CONSTRAINT_ENABLED: {
      actions: 'onPathConstraintEnabled',
    },
    PATH_CONSTRAINT_DISABLED: {
      actions: 'onPathConstraintDisabled',
    },
  },
});

// ============================================================================
// Actor Factory
// ============================================================================

export interface CreateCharacterActorOptions {
  // Options can be added here
}

export function createCharacterActor(
  characterId: string, 
  maxHealth: number,
  _options?: CreateCharacterActorOptions
) {
  return createActor(characterMachine, {
    input: { characterId, maxHealth },
  }).start();
}

// ============================================================================
// Debug Snapshot Helper
// ============================================================================

export function getCharacterDebugSnapshot(actor: AnyActorRef): CharacterDebugSnapshot {
  const snapshot = actor.getSnapshot();
  const context = snapshot.context as CharacterStateContext;
  const stateValue = snapshot.value as { locomotion: string; combat: string | { attacking: string }; health: string };
  
  // Extract combat state (may be nested if attacking)
  let combatState: string;
  let attackPhase: string | undefined;
  
  if (typeof stateValue.combat === 'object') {
    combatState = 'attacking';
    attackPhase = stateValue.combat.attacking;
  } else {
    combatState = stateValue.combat;
  }
  
  return {
    characterId: context.characterId,
    characterDefId: context.characterId.split('_')[0], // Heuristic: extract name from ID
    timestamp: performance.now(),
    
    locomotionState: stateValue.locomotion,
    combatState,
    healthState: stateValue.health,
    
    attackPhase,
    attackId: context.activeAttack?.attackDef.id,
    attackNormalizedTime: context.activeAttack?.normalizedTime,
    attackEventsProcessed: context.activeAttack ? Array.from(context.activeAttack.processedEvents) : undefined,
    
    activeWeapon: context.activeAttack?.weaponInstance ? {
      id: context.activeAttack.weaponInstance.id,
      hitboxEnabled: context.activeAttack.weaponInstance.hitboxEnabled,
      hasHit: context.activeAttack.weaponInstance.hasHit,
    } : undefined,
    
    position: context.position.clone(),
    velocity: context.velocity.clone(),
    isGrounded: context.isGrounded,
    health: context.health,
    facingDirection: context.facingDirection,
  };
}

// ============================================================================
// Type Exports
// ============================================================================

export type CharacterActor = ReturnType<typeof createCharacterActor>;
export type CharacterMachine = typeof characterMachine;
